import { useDataStore } from '@/stores/useDataStore';
import backendApi from '@/axios/backendApi';
import { useToast } from 'vue-toastification'

export const fetchLightCultures = async (): Promise<void> => {
  const dataStore = useDataStore();
  const toast = useToast();

  try {
    // Construit l'URL avec le paramètre active si défini
    const url = '/cultures_light';
    const { data } = await backendApi.get(url);

    // Met à jour le store avec les données récupérées
    dataStore.cultures = data;

  } catch (error: any) {
    console.error('Erreur lors de la récupération des cultures allégées:', error);
    const errorMessage = error.response?.data?.detail || 'Erreur lors de la récupération des cultures';
    toast.error(errorMessage);
  }
};

export const fetchCultureDetails = async (id: number): Promise<Culture> => {
  const dataStore = useDataStore();
  try {
    dataStore.isPrelevementsLoading = true;
    const { data } = await backendApi.get(`/cultures/${id}`);
    dataStore.isPrelevementsLoading = false;
    return data as Culture;
  } catch (error: any) {
    if (error.response) {
      console.error(
        `Erreur lors de la récupération de la culture ${id} : ${error.response.status} - ${error.response.statusText}`,
        error.response.data
      );
    } else {
      console.error(`Erreur réseau ou autre lors de la récupération de la culture ${id} :`, error.message);
    }
    throw error;
  }
};

export const fetchObservationTypes = async () => {
  const dataStore = useDataStore();
  try {
    const { data } = await backendApi.get('/observation_types');
    dataStore.observationTypes = data;
  } catch (error) {
    console.error('Erreur lors du chargement des types d’observation', error);
    throw error;
  }
};

export const fetchPrelevements = async (cultureId: number | undefined = undefined): Promise<void> => {
  const dataStore = useDataStore();
  const toast = useToast();

  try {
    // Choisir l'URL en fonction de la présence de cultureId
    const url = cultureId !== undefined ? `/prelevements/culture/${cultureId}` : '/prelevements';
    const { data } = await backendApi.get(url);

    // Enrichir les prélèvements avec les observations
    const enrichedPrelevements = data.map((p: Prelevement) => {
      const observations = (p.observation_values || []).map((ov: any) => {
        const type = dataStore.observationTypes.find(t => t.id === (ov.observation_type?.id ?? ov.observation_type_id)) ?? null;
        return {
          observation_type_id: type?.id ?? ov.observation_type_id,
          value: ov.value,
          observation_type: type
        };
      });

      return {
        ...p,
        observations,
      };
    });

    // Mettre à jour le store
    dataStore.prelevements = enrichedPrelevements;
  } catch (error: any) {
    console.error('Erreur lors de la récupération des prélèvements:', error);
    const errorMessage = error.response?.data?.detail || 'Erreur lors de la récupération des prélèvements';
    toast.error(errorMessage);
    throw error;
  }
};

export async function fetchPBRs() {
  const dataStore = useDataStore();

  try {
    const { data } = await backendApi.get('/pbrs');
    dataStore.pbrs = data;
  } catch (error: any) {
    if (error.response) {
      console.error(
        `Erreur lors de la récupération des PBRs: ${error.response.status} - ${error.response.statusText}`,
        error.response.data
      );
    } else {
      console.error('Erreur réseau ou autre lors de la récupération des PBRs:', error.message);
    }
    throw error;
  }
};

export const fetchSpecies = async () => {
  const dataStore = useDataStore();

  try {
    const { data } = await backendApi.get('/species');
    const sortedData = data.sort((a: { specie_code: string }, b: { specie_code: string }) => {
      return a.specie_code.localeCompare(b.specie_code);
    });
    dataStore.species = sortedData;
  } catch (error: any) {
    if (error.response) {
      console.error(
        `Erreur lors de la récupération des espèces: ${error.response.status} - ${error.response.statusText}`,
        error.response.data
      );
    } else {
      console.error('Erreur réseau ou autre lors de la récupération des espèces:', error.message);
    }
    throw error;
  }
};

export const fetchStrains = async () => {
  const dataStore = useDataStore();

  try {
    const { data } = await backendApi.get('/strains');
    const sortedData = data.sort((a: { extended_code: string }, b: { extended_code: string }) => {
      return a.extended_code.localeCompare(b.extended_code);
    });
    dataStore.strains = sortedData;
  } catch (error: any) {
    if (error.response) {
      console.error(
        `Erreur lors de la récupération des souches: ${error.response.status} - ${error.response.statusText}`,
        error.response.data
      );
    } else {
      console.error('Erreur réseau ou autre lors de la récupération des souches:', error.message);
    }
    throw error;
  }
};

export async function fetchStartDayCodes() {
  const dataStore = useDataStore();

  try {
    const { data } = await backendApi.get('/startdaycodes');
    dataStore.startdaycodes = data;
  } catch (error: any) {
    if (error.response) {
      console.error(
        `Erreur lors de la récupération des startdaycodes: ${error.response.status} - ${error.response.statusText}`,
        error.response.data
      );
    } else {
      console.error('Erreur réseau ou autre lors de la récupération des startdaycodes:', error.message);
    }
    throw error;
  }
};

export const fetchRecoltes = async (
  annee: number | null = null,
  mois: number | null = null,
  specie: number | null = null,
  cultureIds: number[] | null = null
): Promise<void> => {
  const dataStore = useDataStore();
  const toast = useToast();

  try {
    // Construire l'URL avec les paramètres
    const params = new URLSearchParams();
    if (annee !== null) {
      params.append('year', annee.toString());
    }
    if (mois !== null && annee !== null) {
      params.append('month', mois.toString());
    }
    if (specie !== null) {
      params.append('specie', specie.toString());
    }
    if (cultureIds !== null && cultureIds.length > 0) {
      params.append('culture_ids', cultureIds.join(',')); // Envoyer les IDs comme une liste séparée par des virgules
    }
    const url = `/recoltes${params.toString() ? `?${params.toString()}` : ''}`;

    const { data } = await backendApi.get(url);
    dataStore.recoltes = data;
  } catch (error: any) {
    console.error('Erreur lors de la récupération des récoltes:', error);
    const errorMessage = error.response?.data?.detail || 'Erreur lors de la récupération des récoltes';
    toast.error(errorMessage);
    throw error;
  }
};

export const fetchCultures = async (active: boolean | null, annee: number | null = null, mois: number | null = null, cultureIds?: number[] | null): Promise<void> => {
  const dataStore = useDataStore();
  const toast = useToast();

  try {
    const params = new URLSearchParams();
    let url = '/cultures';

    if (cultureIds && cultureIds.length > 0) {
      params.append('culture_ids', cultureIds.join(','));
    } else if (active !== null) {
      params.append('active', true.toString());
    } else {
      if (annee !== null) {
        params.append('year', annee.toString());
      }
      if (mois !== null) {
        params.append('month', mois.toString());
      }
    }

    const queryString = params.toString() ? `?${params.toString()}` : '';
    url = `${url}${queryString}`;

    const { data } = await backendApi.get(url);
    dataStore.cultures = data;
  } catch (error: any) {
    console.error('Erreur lors de la récupération des cultures:', error);
    const errorMessage = error.response?.data?.detail || 'Erreur lors de la récupération des cultures';
    toast.error(errorMessage);
    throw error;
  }
};

export const fetchMachines = async () => {
  const dataStore = useDataStore();

  try {
    const { data } = await backendApi.get('/machines');
    dataStore.machines = data;
  } catch (error: any) {
    if (error.response) {
      console.error(
        `Erreur lors de la récupération des machines: ${error.response.status} - ${error.response.statusText}`,
        error.response.data
      );
    } else {
      console.error('Erreur réseau ou autre lors de la récupération des machines:', error.message);
    }
    throw error;
  }
};

export const fetchAnalyses = async () => {
  const dataStore = useDataStore();

  try {
    const { data } = await backendApi.get('/analyses');
    dataStore.analyses = data.analyses;
  } catch (error: any) {
    if (error.response) {
      console.error(
        `Erreur lors de la récupération des analyses: ${error.response.status} - ${error.response.statusText}`,
        error.response.data
      );
    } else {
      console.error('Erreur réseau ou autre lors de la récupération des analyses:', error.message);
    }
    throw error;
  }
};

export const fetchEnsemblMetadata = async (strainId: number): Promise<EnsemblSpeciesMetadata[]> => {
  try {
    const { data } = await backendApi.get(`/ensembl_metadata?strain_id=${strainId}`);
    return data as EnsemblSpeciesMetadata[];
  } catch (error: any) {
    if (error.response) {
      console.error(
        `Erreur lors de la récupération des métadonnées Ensembl pour strain ${strainId}: ${error.response.status} - ${error.response.statusText}`,
        error.response.data
      );
    } else {
      console.error('Erreur réseau ou autre lors de la récupération des métadonnées Ensembl:', error.message);
    }
    throw error;
  }
};

export const fetchAlgaebaseMetadata = async (strainId: number): Promise<AlgaebaseSpeciesMetadata[]> => {
  try {
    const { data } = await backendApi.get(`/algaebase_metadata?strain_id=${strainId}`);
    return data as AlgaebaseSpeciesMetadata[];
  } catch (error: any) {
    if (error.response) {
      console.error(
        `Erreur lors de la récupération des métadonnées AlgaeBase pour strain ${strainId}: ${error.response.status} - ${error.response.statusText}`,
        error.response.data
      );
    } else {
      console.error('Erreur réseau ou autre lors de la récupération des métadonnées AlgaeBase:', error.message);
    }
    throw error;
  }
};

export const fetchWikimediaMetadata = async (strainId: number): Promise<WikimediaSpeciesMetadata[]> => {
  try {
    const { data } = await backendApi.get(`/wikimedia_metadata?strain_id=${strainId}`);
    return data as WikimediaSpeciesMetadata[];
  } catch (error: any) {
    if (error.response) {
      console.error(
        `Erreur lors de la récupération des métadonnées Wikimedia pour strain ${strainId}: ${error.response.status} - ${error.response.statusText}`,
        error.response.data
      );
    } else {
      console.error('Erreur réseau ou autre lors de la récupération des métadonnées Wikimedia:', error.message);
    }
    throw error;
  }
};

export const fetchGeoMetadata = async (strainId: number): Promise<GeoSpeciesMetadata[]> => {
  try {
    const { data } = await backendApi.get(`/geo_metadata?strain_id=${strainId}`);
    return data as GeoSpeciesMetadata[];
  } catch (error: any) {
    if (error.response) {
      console.error(
        `Erreur lors de la récupération des métadonnées GEO pour strain ${strainId}: ${error.response.status} - ${error.response.statusText}`,
        error.response.data
      );
    } else {
      console.error('Erreur réseau ou autre lors de la récupération des métadonnées GEO:', error.message);
    }
    throw error;
  }
};